import { Form, Input, Modal, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LeaveRequest } from 'types/leave-request';
import { hasLeaveRequestErrorCode, mapLeaveRequestError } from '../helpers/leave-request-error';

export type ReviewAction = 'approve' | 'reject';

interface Props {
  request: LeaveRequest | null;
  action: ReviewAction | null;
  onClose: () => void;
  onSuccess: (request: LeaveRequest) => void;
  onRaceCondition: (request: LeaveRequest) => void;
}

const actionMeta = {
  approve: {
    title: 'Duyệt phiếu nghỉ phép',
    label: 'Ghi chú duyệt',
    button: 'Duyệt',
    success: 'Đã duyệt phiếu nghỉ phép',
    endpoint: 'approve',
  },
  reject: {
    title: 'Từ chối phiếu nghỉ phép',
    label: 'Lý do từ chối',
    button: 'Từ chối',
    success: 'Đã từ chối phiếu nghỉ phép',
    endpoint: 'reject',
  },
};

const ReviewLeaveRequestModal = ({
  request,
  action,
  onClose,
  onSuccess,
  onRaceCondition,
}: Props) => {
  const [form] = Form.useForm<{ reviewNote?: string }>();
  const [loading, setLoading] = useState(false);
  const meta = action ? actionMeta[action] : null;

  useEffect(() => {
    if (!action) form.resetFields();
  }, [action, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: { reviewNote?: string }) => {
    if (!request || !meta) return;
    const reviewNote = values.reviewNote?.trim();
    setLoading(true);
    try {
      const res: any = await AxiosUtils.patch(
        `${API.leaveRequests}/${request.id}/${meta.endpoint}`,
        reviewNote ? { reviewNote } : {}
      );
      notification.success({ message: meta.success });
      onSuccess(res?.data || request);
      handleClose();
    } catch (err) {
      notification.error({
        message: mapLeaveRequestError(err, { raceCondition: true }),
      });
      if (hasLeaveRequestErrorCode(err, 'LEAVE_REQUEST_NOT_PENDING')) {
        onRaceCondition(request);
        handleClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={meta?.title}
      open={!!request && !!action}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={500}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label={meta?.label}
          name="reviewNote"
          rules={[
            {
              required: action === 'reject',
              message: 'Vui lòng nhập lý do từ chối.',
            },
            { max: 500, message: 'Ghi chú tối đa 500 ký tự.' },
            {
              validator: (_, value) =>
                action !== 'reject' || value?.trim()
                  ? Promise.resolve()
                  : Promise.reject(new Error('Vui lòng nhập lý do từ chối.')),
            },
          ]}
        >
          <Input.TextArea rows={4} maxLength={500} showCount placeholder="Nhập ghi chú" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={meta?.button}
            bgColor={action === 'reject' ? 'primary-red' : 'secondary-green'}
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default ReviewLeaveRequestModal;
